/**
 * Unified card renderer (HEX-103, ADR-0020 §3) — ONE card component, three render
 * modes, all reading the same real `Card` record.
 *
 *   - DISPLAY — full name + cost runes + effect summary + footprint badge. Used
 *     by the shop wares, the drop / death chips, and (as the visual head of) the
 *     hover tooltip.
 *   - PACKED  — sized to fill its grid footprint, density adapting to the area
 *     (1×1 = glyph + gem + name; ≥2 cells add cost runes). Used by the inventory
 *     loadout grid.
 *   - COMBAT  — one uniform "playing card" size regardless of footprint (the
 *     footprint is meaningless in combat, ADR-0021 §1). The cost is shown as a row
 *     of dashed **die-slots**, one per cost predicate, that the overlay fills as
 *     dice are assigned. Used by the combat overlay's player + enemy loadouts.
 *
 * Anatomy per ADR-0020 §3: a 4px glowing **type spine**, a **type-tint wash**
 * background, the bespoke **type glyph** (HEX-102 — `src/render/glyphs`), a
 * faceted **rarity gem** (epic+ frame ticks, legendary foil sheen), the **literal
 * footprint** (mini-grid in display, the card body itself in packed), and
 * **dice-cost runes**. Spine colour, glyph, gem, name and measured footprint are
 * therefore identical in every context — there is no second card look.
 *
 * This module renders the card *face* only. Interactive context (grid-area
 * placement, drag / drop handlers, fed-dice chips, frozen / spent overlays, hover
 * binding) is layered on by each caller onto the returned root, so the engine /
 * interaction logic is untouched (ADR-0020: presentation-only).
 *
 * Colours are CSS custom properties from the Moss & Bone theme (`--t-*`/`--r-*`),
 * so the DOM and Pixi palettes never drift (ADR-0020 §2).
 */

import { createGlyphElement } from '../../render/glyphs';
import type { GlyphName } from '../../render/glyphs';
import { footprint } from '../grid';
import type { Card, CardType, Predicate, Rarity } from '../types';
import { compactPredicate, effectLines, trapLines } from './cardHoverPanel';

/** The three render modes (ADR-0020 §3; combat mode added ADR-0021 §1). */
export type CardMode = 'display' | 'packed' | 'combat';

export interface CardFaceOptions {
  readonly mode: CardMode;
  /** Committed rotation — swaps the measured footprint. Default false. */
  readonly rotated?: boolean;
  /** Display-mode selected/active emphasis (type-colour glow ring). */
  readonly selected?: boolean;
  /** Faded + desaturated (a dimmed / spent / out-of-play card). */
  readonly dim?: boolean;
  /** Override the derived effect summary (display mode). `null` suppresses it. */
  readonly effect?: string | null;
}

/** Type → theme colour custom-property. `die` has no jewel anchor (ADR-0020 §2)
 * so it takes the neutral `--t-die` moss tone. */
const TYPE_COLOUR_VAR: Readonly<Record<CardType, string>> = {
  attack: 'var(--t-attack)',
  defense: 'var(--t-defense)',
  support: 'var(--t-support)',
  recursive: 'var(--t-recursive)',
  trap: 'var(--t-trap)',
  die: 'var(--t-die)',
};

/** Type → display label (kicker). */
const TYPE_LABEL: Readonly<Record<CardType, string>> = {
  attack: 'Attack',
  defense: 'Defense',
  support: 'Support',
  recursive: 'Recursive',
  trap: 'Trap',
  die: 'Die',
};

/** Rarity → theme colour custom-property (the gem / frame / foil tint). */
export const RARITY_COLOUR_VAR: Readonly<Record<Rarity, string>> = {
  common: 'var(--r-common)',
  rare: 'var(--r-rare)',
  epic: 'var(--r-epic)',
  legendary: 'var(--r-legendary)',
};

/** The theme colour custom-property for a card's type spine / glyph. */
export function cardTypeColourVar(card: Card): string {
  return TYPE_COLOUR_VAR[card.type];
}

/**
 * The bespoke glyph for a card's type (HEX-102). The five playable types map
 * one-to-one onto glyph names; `die` cards route through {@link resolveDieGlyph}.
 *
 * This is the EXTENSIBLE die seam (ADR-0020 §3 / ADR-0018): specialist die types
 * (loaded / face-restricted) slot in by branching inside `resolveDieGlyph` on a
 * future `Card` field, with no change to the rest of the renderer.
 */
export function cardGlyphName(card: Card): GlyphName {
  if (card.type === 'die') return resolveDieGlyph(card);
  // attack | defense | support | recursive | trap are all glyph names.
  return card.type;
}

/** Die-type glyph resolution — today every die is the generic d6 (`die` glyph).
 * ADR-0018 specialist dice (loaded / face-restricted) will branch here on the
 * subtype once the `Card` record carries it; callers never special-case dice. */
function resolveDieGlyph(_card: Card): GlyphName {
  return 'die';
}

/** Terse one-line effect summary for the display face. Derived from the real
 * effects (never the mockup's placeholder copy, ADR-0020 §3). Die cards read as
 * pool fuel; the hover panel carries the full mechanical breakdown. */
export function cardEffectSummary(card: Card): string {
  if (card.type === 'die') return 'Adds a die to your pool';
  const parts = [...effectLines(card.effects)];
  if (card.trap) parts.push(...trapLines(card.trap).slice(0, 1));
  return parts.join(' · ');
}

/** A faceted rarity gem (ADR-0020 §3, ported from the handoff `Gem`). Replaces
 * the emoji pip; same gem in every card context + the legend. */
export function buildGem(rarity: Rarity, size = 11): HTMLSpanElement {
  const gem = document.createElement('span');
  gem.className = 'hex6-card__gem';
  gem.dataset.rarity = rarity;
  gem.style.setProperty('--gem-colour', RARITY_COLOUR_VAR[rarity]);
  gem.style.setProperty('--gem-size', `${size}px`);
  gem.setAttribute('aria-hidden', 'true');
  const facet = document.createElement('span');
  facet.className = 'hex6-card__gem-facet';
  gem.appendChild(facet);
  return gem;
}

/** A single dice-cost rune chip: the die glyph + the threshold token (`≥4`,
 * `any`, `=6`, `even`…). */
function buildCostRune(predicate: Predicate): HTMLSpanElement {
  const rune = document.createElement('span');
  rune.className = 'hex6-card__rune';
  rune.appendChild(createGlyphElement('die', { size: 11, className: 'hex6-card__rune-die' }));
  const req = document.createElement('span');
  req.className = 'hex6-card__rune-req';
  req.textContent = compactPredicate(predicate);
  rune.appendChild(req);
  return rune;
}

/** The dice-cost rune row, or null for cost-less cards (traps / die fuel). */
export function buildCostRunes(card: Card): HTMLDivElement | null {
  if (card.cost.length === 0) return null;
  const row = document.createElement('div');
  row.className = 'hex6-card__cost';
  for (const predicate of card.cost) row.appendChild(buildCostRune(predicate));
  return row;
}

/** A single empty die-slot (combat mode) — a dashed cell labelled with the cost
 * predicate it accepts. Addressable via `data-slot-index` so the overlay can fill
 * it and a later animation (HEX-114) can land a die in this specific slot. */
function buildDieSlot(predicate: Predicate, index: number): HTMLDivElement {
  const slot = document.createElement('div');
  slot.className = 'hex6-card__slot';
  slot.dataset.slotIndex = String(index);
  const req = document.createElement('span');
  req.className = 'hex6-card__slot-req';
  req.textContent = compactPredicate(predicate);
  slot.appendChild(req);
  return slot;
}

/** The die-slot row (combat mode) — one dashed slot per cost predicate, or null
 * for cost-less cards (passive traps / die fuel). Replaces the cost-rune row in
 * combat: the slot label *is* the predicate, and the slot is where a die lands
 * (ADR-0021 §1). */
export function buildDieSlots(card: Card): HTMLDivElement | null {
  if (card.cost.length === 0) return null;
  const row = document.createElement('div');
  row.className = 'hex6-card__slots';
  card.cost.forEach((predicate, i) => row.appendChild(buildDieSlot(predicate, i)));
  return row;
}

/** The literal-footprint mini-grid badge (display mode) — one dot per occupied
 * cell, laid out at the card's measured `w×h`. */
export function buildFootprintBadge(card: Card, rotated: boolean): HTMLSpanElement {
  const fp = footprint(card, rotated);
  const badge = document.createElement('span');
  badge.className = 'hex6-card__footprint';
  badge.style.setProperty('--fp-cols', String(fp.cols));
  badge.title = `${fp.cols}×${fp.rows}`;
  for (let i = 0; i < fp.cols * fp.rows; i++) {
    const cell = document.createElement('span');
    cell.className = 'hex6-card__footprint-cell';
    badge.appendChild(cell);
  }
  return badge;
}

/**
 * Render a card face in the given mode. Returns the root `.hex6-card` element;
 * the caller positions it and layers any interactive adornments onto it (the
 * flex body is `.hex6-card__body`).
 */
export function renderCardFace(card: Card, opts: CardFaceOptions): HTMLDivElement {
  const { mode, rotated = false, selected = false, dim = false } = opts;
  const colour = cardTypeColourVar(card);
  const fp = footprint(card, rotated);
  // Combat cards are uniform (footprint-blind, ADR-0021 §1) so they always read at
  // the roomier "big" density; otherwise density follows the measured footprint.
  const big = mode === 'combat' || fp.cols * fp.rows >= 2;

  const root = document.createElement('div');
  root.className = `hex6-card hex6-card--${mode} hex6-card--${card.type} hex6-card--${card.rarity}`;
  root.dataset.type = card.type;
  root.dataset.rarity = card.rarity;
  root.style.setProperty('--card-colour', colour);
  root.style.setProperty('--rarity-colour', RARITY_COLOUR_VAR[card.rarity]);
  if (selected) root.classList.add('hex6-card--selected');
  if (dim) root.classList.add('hex6-card--dim');

  // Type spine (4px glow) + tint wash are drawn in CSS off `--card-colour`.
  const spine = document.createElement('span');
  spine.className = 'hex6-card__spine';
  spine.setAttribute('aria-hidden', 'true');
  root.appendChild(spine);

  // Legendary foil sheen + epic+ frame ticks (decorative, CSS-driven).
  if (card.rarity === 'legendary') {
    const foil = document.createElement('span');
    foil.className = 'hex6-card__foil';
    foil.setAttribute('aria-hidden', 'true');
    root.appendChild(foil);
  }
  if (card.rarity === 'epic' || card.rarity === 'legendary') {
    for (const corner of ['tl', 'br'] as const) {
      const tick = document.createElement('span');
      tick.className = `hex6-card__tick hex6-card__tick--${corner}`;
      tick.setAttribute('aria-hidden', 'true');
      root.appendChild(tick);
    }
  }

  const body = document.createElement('div');
  body.className = 'hex6-card__body';

  const head = document.createElement('div');
  head.className = 'hex6-card__head';
  const glyph = createGlyphElement(cardGlyphName(card), {
    size: big ? 17 : 15,
    colour,
    className: 'hex6-card__glyph',
  });
  const name = document.createElement('span');
  name.className = 'hex6-card__name';
  name.textContent = card.name;
  head.append(glyph, name, buildGem(card.rarity, big ? 11 : 9));
  body.appendChild(head);

  // Cost: combat shows it as die-slots (built below, near the foot); display
  // always shows cost runes; packed only on multi-cell cards (1×1 packed cards
  // stay glyph + gem + name, per ADR-0020 §3 density rule).
  if (mode !== 'combat' && (mode === 'display' || big)) {
    const cost = buildCostRunes(card);
    if (cost) body.appendChild(cost);
  }

  // Effect summary — display + combat (packed cards are size-first).
  if (mode === 'display' || mode === 'combat') {
    const text = opts.effect === undefined ? cardEffectSummary(card) : opts.effect;
    if (text) {
      const effect = document.createElement('div');
      effect.className = 'hex6-card__effect';
      effect.textContent = text;
      body.appendChild(effect);
    }
  }

  // Combat die-slots (ADR-0021 §1) — the cost rendered as dashed drop targets the
  // overlay fills with assigned dice. Sits low, just above the foot.
  if (mode === 'combat') {
    const slots = buildDieSlots(card);
    if (slots) body.appendChild(slots);
  }

  // Foot: type kicker + footprint. Display shows the literal mini-grid; packed
  // (the card already *is* its footprint) shows the terse `w×h`; combat drops the
  // footprint entirely (it carries no meaning there, ADR-0021 §1).
  const foot = document.createElement('div');
  foot.className = 'hex6-card__foot';
  const kicker = document.createElement('span');
  kicker.className = 'hex6-card__kicker';
  kicker.textContent = TYPE_LABEL[card.type];
  foot.appendChild(kicker);
  if (mode === 'display') {
    foot.appendChild(buildFootprintBadge(card, rotated));
  } else if (mode === 'packed') {
    const size = document.createElement('span');
    size.className = 'hex6-card__size';
    size.textContent = `${fp.cols}×${fp.rows}`;
    foot.appendChild(size);
  }
  body.appendChild(foot);

  root.appendChild(body);
  return root;
}
